import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface FavoriteUser {
  id: string;
  position: number;
  favorite_user_id: string;
  created_at: string;
  updated_at: string;
  profile: {
    id: string;
    username: string;
    display_name: string | null;
    profile_image_url: string | null;
    is_admin: boolean | null;
    is_premium: boolean | null;
  };
  recent_bulletin?: {
    id: string;
    image_url: string | null;
    thumbnail_url: string | null;
    content: string;
    created_at: string;
  } | null;
}

export const useFavoriteUsers = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['favorite-users', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_favorites')
        .select(`
          id,
          position,
          favorite_user_id,
          created_at,
          updated_at,
          profiles:favorite_user_id (
            id,
            username,
            display_name,
            profile_image_url,
            is_admin,
            is_premium
          )
        `)
        .eq('user_id', user.id)
        .order('position', { ascending: true });

      if (error) throw error;

      // Fetch recent bulletins for each favorite user
      const favoritesWithPosts = await Promise.all(
        (data || []).map(async (favorite: any) => {
          const { data: recentBulletin } = await supabase
            .from('bulletins')
            .select('id, image_url, thumbnail_url, content, created_at')
            .eq('user_id', favorite.favorite_user_id)
            .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          return {
            id: favorite.id,
            position: favorite.position,
            favorite_user_id: favorite.favorite_user_id,
            created_at: favorite.created_at,
            updated_at: favorite.updated_at,
            profile: Array.isArray(favorite.profiles) ? favorite.profiles[0] : favorite.profiles,
            recent_bulletin: recentBulletin
          };
        })
      );

      return favoritesWithPosts as FavoriteUser[];
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useUserFollowing = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-following', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('followers')
        .select(`
          following_id,
          profiles:following_id (
            id,
            username,
            display_name,
            profile_image_url,
            is_admin,
            is_premium
          )
        `)
        .eq('follower_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};